// Referral program: programs, referrals, flags, agents. Shapes mirror
// apps/backend/src/services/{referral,referralProgram,referralFlag,agent,agentPayout}.service.
import type {
  AgentRejectionReason,
  AgentStatus,
  ReferralFlagStatus,
  ReferralFlagType,
  ReferralProductType,
  ReferralProgram,
  ReferralProgramConfigDto,
  ReferralSide,
  ReferralStatus,
  ReferralTierState,
  ReferredDriverStatus,
  ReferrerType,
} from "@safiri/shared";
import { api, unwrap, cleanParams } from "../lib/axios";
import type { ApiEnvelope, Bilingual } from "../types/api";

export interface Paged<T> {
  items: T[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface ReferralProgramView extends ReferralProgramConfigDto {
  program: ReferralProgram;
  agentMinCashout: number | null;
  versionId: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface ReferralProgramVersion extends ReferralProgramView {
  effectiveFrom: string;
  changedBy: string | null;
  note: string | null;
}

export interface InviteeCard {
  driverId: string;
  name: string;
  phoneNumber: string;
  profilePicUrl: string | null;
  driverStatus: ReferredDriverStatus;
}

export interface Party {
  type: ReferrerType;
  id: string;
  code: string;
  name: string;
}

export interface ReferralRow {
  id: string;
  program: ReferralProgram;
  productType: ReferralProductType;
  /** Locked when the admin approves the referred driver; null until then. */
  rideTypeId: string | null;
  rideTypeName: string | null;
  lastTripAt: string | null;
  inviter: Party;
  invitee: InviteeCard;
  tripCount: number;
  status: ReferralStatus;
  amountPaid: number;
  maxAmount: number;
  inviteePaid: number;
  createdAt: string;
}

export interface TierView {
  key: string;
  side: ReferralSide;
  index: number;
  milestone: string;
  tripCount: number;
  label: Bilingual;
  amount: number;
  windowDays: number;
  windowFrom: string;
  deadline: string | null;
  daysRemaining: number | null;
  state: ReferralTierState;
  paidAt: string | null;
  commissionOffset: number;
  withdrawable: number;
  expiredAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  reversal: { at: string; reason: string; byName: string | null } | null;
  transactionID?: string | null;
}

export interface ReferralDetail {
  id: string;
  program: ReferralProgram;
  productType: ReferralProductType;
  rideTypeId: string | null;
  rideTypeName: string | null;
  attachedBy: "driver" | "admin";
  inviter: Party;
  invitee: InviteeCard;
  signupAt: string;
  activatedAt: string | null;
  firstTripAt: string | null;
  lastTripAt: string | null;
  tripCount: number;
  status: ReferralStatus;
  closedAt: string | null;
  closeReason: string | null;
  inviterProgress: { max: number; paid: number; possible: number };
  inviteeProgress: { max: number; paid: number; possible: number };
  tiers: TierView[];
  history: { at: string; action: string; byName: string | null; reason: string | null; detail: Record<string, unknown> | null }[];
  createdAt: string;
}

export interface InvitedRow extends InviteeCard {
  id: string;
  program: ReferralProgram;
  tripCount: number;
  lastTripAt: string | null;
  status: ReferralStatus;
  paid: number;
  max: number;
  signedUpAt: string;
}

export interface DriverReferrals {
  code: string | null;
  invited: InvitedRow[];
  invitedBy: (Party & { referralId: string; status: ReferralStatus; tierOnePaid: boolean }) | null;
}

export interface ReferralSummary {
  tiers: { program: ReferralProgram; side: ReferralSide; state: ReferralTierState; count: number; amount: number }[];
  statuses: { program: ReferralProgram; status: ReferralStatus; count: number }[];
  openFlags: number;
}

export interface ReferralFlag {
  _id: string;
  type: ReferralFlagType;
  status: ReferralFlagStatus;
  summary: string;
  referralIDs: string[];
  agentIDs: string[];
  driverIDs: string[];
  detail: Record<string, unknown>;
  occurrences: number;
  resolvedAt: string | null;
  resolvedByName: string | null;
  resolution: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AgentRow {
  id: string;
  name: string;
  phoneNumber: string;
  email: string | null;
  region: string | null;
  district: string | null;
  status: AgentStatus;
  referralCode: string | null;
  submittedAt: string | null;
  waitingHours: number | null;
  walletBalance: number;
  createdByAdmin: boolean;
  invitePending: boolean;
  createdAt: string;
}

export interface AgentDuplicate {
  kind: string;
  ownerType: "agent" | "driver";
  ownerId: string;
  name: string;
  status: string | null;
}

export interface AgentDetail {
  id: string;
  name: string;
  phoneNumber: string;
  /** Optional and unverified since 2026-10-29. */
  email: string | null;
  phoneVerified: boolean;
  language: string;
  dob: string | null;
  gender: string | null;
  region: string | null;
  district: string | null;
  street: string | null;
  altPhoneNumber: string | null;
  heardFrom: string | null;
  referringStaffName: string | null;
  idDocumentUrl: string | null;
  profilePhotoUrl: string | null;
  status: AgentStatus;
  submittedAt: string | null;
  approvedAt: string | null;
  rejection: { reasons: { code: AgentRejectionReason; label: Bilingual }[]; comment: string | null; at: string } | null;
  referralCode: string | null;
  termsAccepted: boolean;
  walletBalance: number;
  totalRewardsCredited: number;
  statusHistory: { status: AgentStatus; at: string; reason: string | null; byName: string | null }[];
  createdByAdmin: { name: string | null; at: string } | null;
  invite: { sentAt: string; expiresAt: string; acceptedAt: string | null } | null;
  deletedAt: string | null;
  duplicates: AgentDuplicate[];
  summary: {
    total: number;
    byStatus: Record<string, number>;
    byDriverStatus: Record<string, number>;
    rewardsCredited: number;
    rewardsStillPossible: number;
  };
  payouts: { id: string; amount: number; status: string; destination: string | null; receipt: string | null; createdAt: string }[];
}

export interface AgentWalletRow {
  id: string;
  type: "reward" | "reversal" | "cashout" | "forfeit";
  amount: number;
  signedAmount: number;
  status: string;
  title: Bilingual;
  inviteeName: string | null;
  tier: string | null;
  reason: string | null;
  destination: string | null;
  receipt: string | null;
  createdAt: string;
}

export interface CreateAgentBody {
  name: string;
  phoneNumber: string;
  /** Optional: blank means the agent has no email. */
  email?: string | null;
  language: "en" | "sw";
  dob: string;
  gender: string;
  region: string;
  district: string;
  street: string;
  idDocumentKey: string;
  profilePhotoKey: string;
  altPhoneNumber?: string | null;
  heardFrom?: string | null;
  referringStaffName?: string | null;
}

async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const res = await api.get<ApiEnvelope<T>>(url, { params: params ? cleanParams(params) : undefined });
  return unwrap(res);
}
async function post<T>(url: string, body?: unknown): Promise<T> {
  return unwrap(await api.post<ApiEnvelope<T>>(url, body ?? {}));
}
async function patch<T>(url: string, body?: unknown): Promise<T> {
  return unwrap(await api.patch<ApiEnvelope<T>>(url, body ?? {}));
}

export const referralsApi = {
  programs: () => get<ReferralProgramView[]>("/admin/referrals/programs"),
  programHistory: (program: ReferralProgram) => get<ReferralProgramVersion[]>(`/admin/referrals/programs/${program}/history`),
  updateProgram: (program: ReferralProgram, body: ReferralProgramConfigDto & { agentMinCashout?: number | null; note?: string }) =>
    patch<ReferralProgramView>(`/admin/referrals/programs/${program}`, body),

  summary: () => get<ReferralSummary>("/admin/referrals/summary"),
  list: (q: Record<string, unknown>) => get<Paged<ReferralRow>>("/admin/referrals", q),
  detail: (id: string) => get<ReferralDetail>(`/admin/referrals/${id}`),
  driver: (driverId: string) => get<DriverReferrals>(`/admin/referrals/drivers/${driverId}`),
  attach: (driverId: string, code: string, reason: string) => post<ReferralDetail>("/admin/referrals/attach", { driverId, code, reason }),
  disable: (id: string, reason: string) => post<ReferralDetail>(`/admin/referrals/${id}/disable`, { reason }),
  disableForParty: (body: { role: "inviter" | "invitee"; type: ReferrerType; id: string; reason: string }) =>
    post<{ disabled: number }>("/admin/referrals/disable-party", body),
  reverse: (id: string, tierKey: string, reason: string) => post<ReferralDetail>(`/admin/referrals/${id}/tiers/${tierKey}/reverse`, { reason }),

  flags: (q: Record<string, unknown>) => get<Paged<ReferralFlag>>("/admin/referrals/flags", q),
  resolveFlag: (id: string, status: ReferralFlagStatus, note: string) => post<ReferralFlag>(`/admin/referrals/flags/${id}/resolve`, { status, note }),
};

export const agentsApi = {
  list: (q: Record<string, unknown>) => get<Paged<AgentRow>>("/admin/agents", q),
  detail: (id: string) => get<AgentDetail>(`/admin/agents/${id}`),
  referrals: (id: string) => get<Paged<ReferralRow>>(`/admin/agents/${id}/referrals`),
  wallet: (id: string) => get<{ balance: number; history: AgentWalletRow[] }>(`/admin/agents/${id}/wallet`),
  create: (body: CreateAgentBody) => post<AgentDetail>("/admin/agents", body),
  async upload(kind: "idDocument" | "profilePhoto", file: File) {
    const form = new FormData();
    form.append("file", file);
    const res = await api.post<ApiEnvelope<{ key: string; url: string | null }>>(`/admin/agents/uploads/${kind}`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return unwrap(res);
  },
  approve: (id: string) => post<AgentDetail>(`/admin/agents/${id}/approve`),
  reject: (id: string, reasons: AgentRejectionReason[], comment: string | null) => post<AgentDetail>(`/admin/agents/${id}/reject`, { reasons, comment }),
  resendInvite: (id: string) => post<AgentDetail>(`/admin/agents/${id}/resend-invite`),
  suspend: (id: string, reason: string) => post<AgentDetail>(`/admin/agents/${id}/suspend`, { reason }),
  reactivate: (id: string, reason: string) => post<AgentDetail>(`/admin/agents/${id}/reactivate`, { reason }),
  editLocked: (id: string, body: Record<string, unknown>) => patch<AgentDetail>(`/admin/agents/${id}/locked`, body),
};
